import { Color, Spacing } from '@persona-id/design-tokens';
import { CheckIcon, CircleHelpIcon, XIcon } from '@persona-id/design-tokens/icons';
import { ChangeEvent, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { MatchFuzzinessLevel, MatchPreset } from 'dashboard/models/report-template-version';
import Checkbox from 'design-system/components/Checkbox';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Slider from 'design-system/components/Slider';
import Spacer from 'design-system/components/Spacer';
import { Paragraph, Span } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';
import { FontWeight } from 'design-system/variables/text';
import useToggle from 'lib/hooks/useToggle';

export interface NameMatchingFormData extends Record<string, unknown> {
  'match-preset': MatchPreset;
  'aka-exact-enabled'?: boolean;
  'partial-names-enabled': boolean;
  'equivalent-names-enabled'?: boolean;
  'match-joined'?: boolean;
  'match-first-last'?: boolean;
  'fuzziness-level'?: MatchFuzzinessLevel;
}

interface NameMatchingFormSectionProps<T extends NameMatchingFormData = NameMatchingFormData> {
  defaultValues: T;
  disabled: boolean;
  isBusiness?: boolean;
  isWatchlist?: boolean;
}

const StyledSpan = styled(Span)`
  font-weight: 500;
`;

export const NameMatchingFormSection = <T extends NameMatchingFormData>({
  defaultValues,
  disabled,
  isBusiness = false,
  isWatchlist = false,
}: NameMatchingFormSectionProps<T>) => {
  const { register, control, setValue, watch } = useFormContext<NameMatchingFormData>();
  const [fuzzinessEnabled, { toggle: toggleFuzziness }] = useToggle(
    defaultValues['fuzziness-level'] != null,
  );

  const matchPreset = watch('match-preset', defaultValues['match-preset']);
  const fuzzinessLevel = watch('fuzziness-level', defaultValues['fuzziness-level']);
  const partialNamesEnabled = watch(
    'partial-names-enabled',
    defaultValues['partial-names-enabled'],
  );

  const tooltipDescriptions = isBusiness ? CONFIGURATION_TOOLTIPS_BUSINESS : CONFIGURATION_TOOLTIPS;

  useEffect(() => {
    if (fuzzinessEnabled && fuzzinessLevel == null) {
      setValue('fuzziness-level', MatchFuzzinessLevel.LOW);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuzzinessEnabled, fuzzinessLevel]);

  return (
    <NameMatchingSection>
      <NameMatchingColumn>
        <RadioGroup>
          <Radio
            label={
              <RadioLabelWrapper>
                <RadioLabelTitle>Exact</RadioLabelTitle>
                <Spacer height={0.5} />
                <RadioLabelSubTitle color='secondary'>
                  Exact match on name or AKAs in article or source
                </RadioLabelSubTitle>
              </RadioLabelWrapper>
            }
            {...register('match-preset')}
            value={MatchPreset.EXACT}
            disabled={disabled}
          />
          <Spacer height={1.5} />
          <Radio
            label={
              <RadioLabelWrapper>
                <RadioLabelTitle>Strict</RadioLabelTitle>
                <Spacer height={0.5} />
                <RadioLabelSubTitle color='secondary'>
                  Match on name and AKAs, allowing for subset name and initial matching. Recommended
                  setting.
                </RadioLabelSubTitle>
              </RadioLabelWrapper>
            }
            {...register('match-preset')}
            value={MatchPreset.STRICT}
            disabled={disabled}
          />
          <Spacer height={1.5} />
          <Radio
            label={
              <RadioLabelWrapper>
                <RadioLabelTitle>Tolerant</RadioLabelTitle>
                <Spacer height={0.5} />
                <RadioLabelSubTitle color='secondary'>
                  Allow for equivalent name matching, inclusive of all settings. Allows room for
                  typos.
                </RadioLabelSubTitle>
              </RadioLabelWrapper>
            }
            {...register('match-preset')}
            value={MatchPreset.TOLERANT}
            disabled={disabled}
          />
          <Spacer height={1.5} />
          <Radio
            label={
              <RadioLabelWrapper>
                <RadioLabelTitle>Custom</RadioLabelTitle>
                <Spacer height={0.5} />

                <RadioLabelSubTitle color='secondary'>
                  Set your own custom match profile.
                </RadioLabelSubTitle>
              </RadioLabelWrapper>
            }
            {...register('match-preset')}
            value={MatchPreset.CUSTOM}
            disabled={disabled}
          />
        </RadioGroup>
      </NameMatchingColumn>
      {matchPreset !== MatchPreset.CUSTOM ? (
        getMatchProfile(matchPreset, isBusiness)
      ) : (
        <CustomConfig>
          <Checkbox
            label={
              <DisplayWithTooltip
                displayText={
                  isWatchlist ? MatchConfigurationLabel.ExactAndAkas : MatchConfigurationLabel.Exact
                }
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                tooltipText={
                  isWatchlist
                    ? tooltipDescriptions[MatchConfigurationLabel.ExactAndAkas]
                    : tooltipDescriptions[MatchConfigurationLabel.Exact]
                }
              />
            }
            checked={true}
            disabled
          />
          {!isWatchlist && (
            <Controller
              name='aka-exact-enabled'
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Checkbox
                  label={
                    <DisplayWithTooltip
                      displayText={MatchConfigurationLabel.Akas}
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      tooltipText={tooltipDescriptions[MatchConfigurationLabel.Akas]}
                    />
                  }
                  checked={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={disabled}
                />
              )}
            />
          )}
          <Controller
            name='partial-names-enabled'
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <Checkbox
                label={
                  <DisplayWithTooltip
                    displayText={MatchConfigurationLabel.PartialNames}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    tooltipText={tooltipDescriptions[MatchConfigurationLabel.PartialNames]}
                  />
                }
                checked={value}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const checked = e.target.checked;
                  if (checked) {
                    setValue('aka-exact-enabled', true);
                  } else {
                    setValue('equivalent-names-enabled', false);
                  }
                  return onChange(e);
                }}
                onBlur={onBlur}
                disabled={disabled}
              />
            )}
          />
          {!isBusiness && (
            <Controller
              name='equivalent-names-enabled'
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Checkbox
                  label={
                    <DisplayWithTooltip
                      displayText={MatchConfigurationLabel.EquivalentNames}
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      tooltipText={tooltipDescriptions[MatchConfigurationLabel.EquivalentNames]}
                    />
                  }
                  checked={value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const checked = e.target.checked;
                    if (checked) {
                      setValue('partial-names-enabled', true);
                      setValue('aka-exact-enabled', true);
                    }
                    return onChange(e);
                  }}
                  onBlur={onBlur}
                  disabled={disabled}
                />
              )}
            />
          )}
          {isWatchlist && (
            <>
              <Controller
                name='match-joined'
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Checkbox
                    label={
                      <DisplayWithTooltip
                        displayText={MatchConfigurationLabel.JoinedNames}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        tooltipText={tooltipDescriptions[MatchConfigurationLabel.JoinedNames]}
                      />
                    }
                    checked={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                name='match-first-last'
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Checkbox
                    label={
                      <DisplayWithTooltip
                        displayText={MatchConfigurationLabel.FirstLastNames}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        tooltipText={tooltipDescriptions[MatchConfigurationLabel.FirstLastNames]}
                      />
                    }
                    checked={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    disabled={disabled}
                  />
                )}
              />
            </>
          )}
          <div>
            <Spacer height={2} />
            <Checkbox
              label={
                <DisplayWithTooltip
                  displayText={MatchConfigurationLabel.Fuzziness}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  tooltipText={tooltipDescriptions[MatchConfigurationLabel.Fuzziness]}
                />
              }
              checked={fuzzinessEnabled}
              onChange={toggleFuzziness}
              disabled={disabled}
            />
            {fuzzinessEnabled && (
              <>
                <Spacer height={3} />
                <Controller
                  control={control}
                  name='fuzziness-level'
                  render={({ field: { onChange, value } }) => (
                    <SliderWrapper>
                      <Slider
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        defaultValue={convertFuzzinessToSliderValue(value)}
                        max={4}
                        min={0}
                        step={2}
                        showLabels={false}
                        hideTooltips={true}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        onChange={(sliderNumber: number) =>
                          onChange(convertSliderValueToFuzziness(sliderNumber))
                        }
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        value={convertFuzzinessToSliderValue(value)}
                        aria-label='Fuzziness Level'
                        disabled={disabled}
                      />
                      <Spacer height={1} />
                      <SliderLabels>
                        <TextButton
                          type='button'
                          id='name-matching-form__low'
                          tabIndex={-1}
                          onClick={() => onChange(MatchFuzzinessLevel.LOW)}
                        >
                          Low
                        </TextButton>
                        <TextButton
                          type='button'
                          id='name-matching-form__medium'
                          tabIndex={-1}
                          onClick={() => onChange(MatchFuzzinessLevel.MEDIUM)}
                        >
                          Medium
                        </TextButton>
                        <TextButton
                          type='button'
                          id='name-matching-form__high'
                          tabIndex={-1}
                          onClick={() => onChange(MatchFuzzinessLevel.HIGH)}
                        >
                          High
                        </TextButton>
                      </SliderLabels>
                      <Spacer height={0.5} />
                      <Paragraph size='sm'>
                        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                        {getFuzzinessExample(fuzzinessLevel, isWatchlist, partialNamesEnabled)}
                      </Paragraph>
                    </SliderWrapper>
                  )}
                />
              </>
            )}
          </div>
        </CustomConfig>
      )}
    </NameMatchingSection>
  );
};

const getMatchProfile = (matchPreset: MatchPreset, isBusiness: boolean) => {
  const allPresets = [MatchPreset.EXACT, MatchPreset.STRICT, MatchPreset.TOLERANT];
  const tooltipDescriptions = isBusiness ? CONFIGURATION_TOOLTIPS_BUSINESS : CONFIGURATION_TOOLTIPS;

  if (!allPresets.includes(matchPreset)) {
    return null;
  }

  let profileConfigs = null;
  switch (matchPreset) {
    case 'exact':
      profileConfigs = {
        [MatchConfigurationLabel.Exact]: true,
        [MatchConfigurationLabel.Akas]: true,
        [MatchConfigurationLabel.PartialNames]: false,
        [MatchConfigurationLabel.JoinedNames]: false,
        [MatchConfigurationLabel.FirstLastNames]: false,
        [MatchConfigurationLabel.EquivalentNames]: false,
        [MatchConfigurationLabel.Fuzziness]: null,
      };
      break;
    case 'strict':
      profileConfigs = {
        [MatchConfigurationLabel.Exact]: true,
        [MatchConfigurationLabel.Akas]: true,
        [MatchConfigurationLabel.PartialNames]: true,
        [MatchConfigurationLabel.JoinedNames]: true,
        [MatchConfigurationLabel.FirstLastNames]: true,
        [MatchConfigurationLabel.EquivalentNames]: false,
        [MatchConfigurationLabel.Fuzziness]: null,
      };
      break;
    case 'tolerant':
    default:
      profileConfigs = {
        [MatchConfigurationLabel.Exact]: true,
        [MatchConfigurationLabel.Akas]: true,
        [MatchConfigurationLabel.PartialNames]: true,
        [MatchConfigurationLabel.JoinedNames]: true,
        [MatchConfigurationLabel.FirstLastNames]: true,
        [MatchConfigurationLabel.EquivalentNames]: true,
        [MatchConfigurationLabel.Fuzziness]: MatchFuzzinessLevel.MEDIUM,
      };
  }

  const fuzzinessText = `${MatchConfigurationLabel.Fuzziness}${
    profileConfigs[MatchConfigurationLabel.Fuzziness] != null
      ? ': ' + profileConfigs[MatchConfigurationLabel.Fuzziness]
      : ''
  }`;

  return (
    <PresetConfig>
      <StaticCheckbox>
        {profileConfigs[MatchConfigurationLabel.Exact] ? (
          <CheckIcon color={Color.CoreGreen800} />
        ) : (
          <XIcon />
        )}
        <DisplayWithTooltip
          displayText={MatchConfigurationLabel.Exact}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          tooltipText={tooltipDescriptions[MatchConfigurationLabel.Exact]}
        />
      </StaticCheckbox>
      <StaticCheckbox>
        {profileConfigs[MatchConfigurationLabel.Akas] ? (
          <CheckIcon color={Color.CoreGreen800} />
        ) : (
          <XIcon />
        )}
        <DisplayWithTooltip
          displayText={MatchConfigurationLabel.Akas}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          tooltipText={tooltipDescriptions[MatchConfigurationLabel.Akas]}
        />
      </StaticCheckbox>
      <StaticCheckbox>
        {profileConfigs[MatchConfigurationLabel.PartialNames] ? (
          <CheckIcon color={Color.CoreGreen800} />
        ) : (
          <XIcon />
        )}
        <DisplayWithTooltip
          displayText={MatchConfigurationLabel.PartialNames}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          tooltipText={tooltipDescriptions[MatchConfigurationLabel.PartialNames]}
        />
      </StaticCheckbox>
      <StaticCheckbox>
        {profileConfigs[MatchConfigurationLabel.JoinedNames] ? (
          <CheckIcon color={Color.CoreGreen800} />
        ) : (
          <XIcon />
        )}
        <DisplayWithTooltip
          displayText={MatchConfigurationLabel.JoinedNames}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          tooltipText={tooltipDescriptions[MatchConfigurationLabel.JoinedNames]}
        />
      </StaticCheckbox>
      <StaticCheckbox>
        {profileConfigs[MatchConfigurationLabel.FirstLastNames] ? (
          <CheckIcon color={Color.CoreGreen800} />
        ) : (
          <XIcon />
        )}
        <DisplayWithTooltip
          displayText={MatchConfigurationLabel.FirstLastNames}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          tooltipText={tooltipDescriptions[MatchConfigurationLabel.FirstLastNames]}
        />
      </StaticCheckbox>
      {!isBusiness && (
        <StaticCheckbox>
          {profileConfigs[MatchConfigurationLabel.EquivalentNames] ? (
            <CheckIcon color={Color.CoreGreen800} />
          ) : (
            <XIcon />
          )}
          <DisplayWithTooltip
            displayText={MatchConfigurationLabel.EquivalentNames}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            tooltipText={tooltipDescriptions[MatchConfigurationLabel.EquivalentNames]}
          />
        </StaticCheckbox>
      )}
      <StaticCheckbox>
        {profileConfigs[MatchConfigurationLabel.Fuzziness] ? (
          <CheckIcon color={Color.CoreGreen800} />
        ) : (
          <XIcon />
        )}
        <DisplayWithTooltip
          displayText={fuzzinessText}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          tooltipText={tooltipDescriptions[MatchConfigurationLabel.Fuzziness]}
        />
      </StaticCheckbox>
    </PresetConfig>
  );
};

const DisplayWithTooltip = ({
  displayText,
  tooltipText,
}: {
  displayText: string;
  tooltipText: string;
}) => {
  return (
    <WrapperWithTooltip>
      <span>{displayText}</span>
      <Tooltip side='top' iconTrigger={<CircleHelpIcon size={14} color={Color.CoreSlate800} />}>
        <TooltipWrapper>{tooltipText}</TooltipWrapper>
      </Tooltip>
    </WrapperWithTooltip>
  );
};

const getFuzzinessExample = (
  fuzzinessLevel: MatchFuzzinessLevel,
  isWatchlist: boolean,
  isPartial: boolean,
) => {
  const isPartialIndicatorText = isPartial ? <StyledSpan> per word </StyledSpan> : ' ';

  const nonWatchlistTypoCount: Record<string, number> = {
    high: 3,
    medium: 5,
    low: 13,
  };

  const watchlistTypoCount: Record<string, number> = {
    high: 3,
    medium: isPartial ? 5 : 10,
    low: 18,
  };

  if (isWatchlist) {
    return (
      <span role='definition'>
        1 typo allowed{isPartialIndicatorText}at a minimum of {watchlistTypoCount[fuzzinessLevel]}{' '}
        characters
      </span>
    );
  } else {
    return (
      <span role='definition'>
        1 typo allowed <StyledSpan>per word</StyledSpan> at a minimum of{' '}
        {nonWatchlistTypoCount[fuzzinessLevel]} characters
      </span>
    );
  }
};

const convertSliderValueToFuzziness = (numberValue: number): MatchFuzzinessLevel => {
  switch (numberValue) {
    case 4:
      return MatchFuzzinessLevel.HIGH;
    case 2:
      return MatchFuzzinessLevel.MEDIUM;
    case 0:
    default:
      return MatchFuzzinessLevel.LOW;
  }
};

const convertFuzzinessToSliderValue = (level: MatchFuzzinessLevel): number => {
  switch (level) {
    case MatchFuzzinessLevel.HIGH: {
      return 4;
    }
    case MatchFuzzinessLevel.MEDIUM: {
      return 2;
    }
    case MatchFuzzinessLevel.LOW:
    default:
      return 0;
  }
};

enum MatchConfigurationLabel {
  Exact = 'Exact names',
  ExactAndAkas = 'Exact names and AKAs',
  Akas = 'AKAs',
  PartialNames = 'Partial names',
  EquivalentNames = 'Equivalent names',
  JoinedNames = 'Joined names',
  FirstLastNames = 'First last names',
  Fuzziness = 'Fuzziness (typos)',
}

const CONFIGURATION_TOOLTIPS: { [key in MatchConfigurationLabel]?: string } = {
  [MatchConfigurationLabel.Exact]:
    'Match on exact name extracted from source. This configuration cannot be removed.',
  [MatchConfigurationLabel.ExactAndAkas]:
    'Match on exact name and AKA (an alternative name a person is known by) extracted from source. This configuration cannot be removed.',
  [MatchConfigurationLabel.Akas]:
    'Exact match on an AKA (an alternative name a person is known by). Inclusive of exact name setting.',
  [MatchConfigurationLabel.PartialNames]:
    'Match on subset of words (e.g. "John Bob Smith" results in a match for "John Smith"), and inclusive of initials (e.g. "J. B. Smith" results in a match for "John Bob Smith"). Inclusive of AKAs and exact name settings.',
  [MatchConfigurationLabel.EquivalentNames]:
    'Match on hypocorisms (nicknames) and abbreviations. Inclusive of partial names, AKAs, and exact name settings.',
  [MatchConfigurationLabel.JoinedNames]:
    'Match only the letters in the order provided, ignoring punctuation and whitespace. (e.g. "Jo Edoe" results in a match for "Joe Doe")',
  [MatchConfigurationLabel.FirstLastNames]:
    'Match by comparing only the first and last terms. (e.g. "Joe Smith Doe" results in a match for "Joe Doe")',
  [MatchConfigurationLabel.Fuzziness]:
    'Fuzziness allows for typos based on the length of a word. A low fuzziness allows an edit on longer words, whereas a high fuzziness allows an edit on shorter words.',
};

const CONFIGURATION_TOOLTIPS_BUSINESS: { [key in MatchConfigurationLabel]?: string } = {
  [MatchConfigurationLabel.Exact]:
    'Match on exact name extracted from source. This configuration cannot be removed.',
  [MatchConfigurationLabel.Akas]:
    'Exact match on an AKA (an alternative name a company is known by). Inclusive of exact name setting.',
  [MatchConfigurationLabel.PartialNames]:
    'Match on subset of words (e.g. "Fin Tech Company" results in a match for "Fin Tech"), and ignores suffixes like Ltd. Inclusive of AKAs and exact name settings.',
  [MatchConfigurationLabel.Fuzziness]:
    'Fuzziness allows for typos based on the length of a word. A low fuzziness allows an edit on longer words, whereas a high fuzziness allows an edit on shorter words.',
};

const RadioLabelWrapper = styled.span`
  display: flex;
  flex-direction: column;
  margin-left: ${Spacing[2]};
`;

const RadioLabelTitle = styled(Span)`
  ${paragraphMixins.lgBold}
`;

const RadioLabelSubTitle = styled(Span)`
  ${paragraphMixins.md}
  font-weight: ${FontWeight.Regular};
`;

const NameMatchingSection = styled.div`
  display: flex;
`;

const NameMatchingColumn = styled.div`
  flex: 1 0 50%;
`;

const CustomConfig = styled(NameMatchingColumn)`
  margin-left: ${Spacing[4]};
  display: grid;
  grid-template-rows: repeat(6, 22px) auto;
  grid-gap: ${Spacing[3]};
`;

const WrapperWithTooltip = styled.span`
  display: grid;
  grid-template-columns: auto ${Spacing[4]};
  grid-gap: ${Spacing[1]};
  align-items: center;
  color: ${({ theme }) => theme.color.text.primary};
  ${paragraphMixins.md}
`;

const TooltipWrapper = styled.span`
  max-width: 400px;
`;

const SliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 300px;
  margin-left: ${Spacing[5]};
`;

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;

  ${paragraphMixins.md}
`;

const PresetConfig = styled(NameMatchingColumn)`
  margin-left: ${Spacing[4]};
  display: grid;
  grid-template-rows: repeat(6, 22px);
  grid-gap: ${Spacing[3]};
  align-items: start;
`;

const StaticCheckbox = styled.div`
  display: flex;
  align-items: center;

  & > svg {
    margin-right: ${Spacing[3]};
  }
`;

const TextButton = styled.button`
  background-color: transparent;
  cursor: pointer;
`;
